import { useRef, useState } from "react";
import { Eye, Loader2, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useBolUpload } from "@/hooks/useBolUpload";
import { OrderNotesCard, useOrderNoteCount } from "@/components/orders/OrderNotesCard";
import { ChecklistDocViewer } from "./ChecklistDocViewer";
import { ChecklistRow } from "./ChecklistRow";

interface Props {
  orderId: string;
  orderUniqueId?: string | null;
  driverName?: string;
}

/**
 * Unified BOL + Notes + Tender documents block for the Driver assignment
 * detail page. Renders in the same checklist-row style as the trailer
 * readiness checklist so the interaction is consistent regardless of
 * whether the leg is YMS-tracked or non-YMS.
 */
export function DriverDocsChecklist({ orderId, orderUniqueId, driverName }: Props) {
  const bolInput = useRef<HTMLInputElement>(null);
  const [bolViewerOpen, setBolViewerOpen] = useState(false);
  const [tenderViewerOpen, setTenderViewerOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const { uploadFiles: uploadBol, isUploading: bolUploading } = useBolUpload({
    orderId,
    orderUniqueId: orderUniqueId ?? undefined,
    mode: "driver",
    changedBy: driverName,
  });

  const { data: bolCount = 0 } = useQuery({
    queryKey: ["bol-images-count", orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_bol_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", orderId);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: tenderCount = 0 } = useQuery({
    queryKey: ["tender-images-count", orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_tender_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", orderId);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: noteCount = 0 } = useOrderNoteCount(orderId);

  return (
    <div className="rounded-md border divide-y bg-card overflow-hidden">
      <ChecklistRow
        complete={bolCount > 0}
        label="Upload BOL"
        viewCount={bolCount}
        onViewClick={() => setBolViewerOpen(true)}
        onClick={() => bolInput.current?.click()}
        trailing={
          bolUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )
        }
      />
      <input
        ref={bolInput}
        type="file"
        accept="image/*,.pdf,application/pdf"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void uploadBol(files);
          if (bolInput.current) bolInput.current.value = "";
        }}
      />

      <ChecklistRow
        complete={false}
        optional
        label="Add notes (optional)"
        viewCount={noteCount}
        onViewClick={() => setNotesOpen(true)}
        onClick={() => setNotesOpen(true)}
        trailing={<Plus className="h-5 w-5 text-muted-foreground" />}
      />

      {tenderCount > 0 && (
        <ChecklistRow
          complete
          label="View tender documents"
          viewCount={tenderCount}
          onViewClick={() => setTenderViewerOpen(true)}
          onClick={() => setTenderViewerOpen(true)}
          trailing={<Eye className="h-5 w-5 text-muted-foreground" />}
        />
      )}

      <ChecklistDocViewer
        open={bolViewerOpen}
        onOpenChange={setBolViewerOpen}
        scopeId={orderId}
        kind="bol"
      />
      <ChecklistDocViewer
        open={tenderViewerOpen}
        onOpenChange={setTenderViewerOpen}
        scopeId={orderId}
        kind="tender"
      />

      <Drawer open={notesOpen} onOpenChange={setNotesOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Notes</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[70vh] overflow-auto">
            <OrderNotesCard orderId={orderId} compact hideHeader />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}